import { Controller, type Control, type FieldValues, type Path } from "react-hook-form";
import Password from "./Password/Password";
import Select from "./Select/Select";
import FileInput from "./FileInput/FileInput";
import Checkbox from "./Checkbox/Checkbox";
import Range from "./Range/Range";
import TextArea from "./TextArea/TextArea";
import DateInput from "./Date/DateInput";
import MultiAssign from "./MultiAssign/MultiAssign";
import RadioGroup from "./Radio/Radio";
import Input from "./Input/Input";
import Toggle from "./Toggle/Toggle";
import TimePicker from "./TimePicker/TimePicker";
import RadioAssign from "./RadioAssign/RadioAssign";
import MultipleFileInput from "./MultipleFileInput/MultipleFileInput";
import AutoComplete from "./AutoComplete/AutoComplete";
import ChipInput from "./ChipInput/ChipInput";

interface FormControlProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  rules?: any;
  controlType: string;
  slotProps?: any;
  [key: string]: any;
}

const FormControl = <T extends FieldValues>({
  name,
  control,
  rules,
  controlType,
  slotProps,
  ...rest
}: FormControlProps<T>) => {
  const shouldSanitize = ["input", "textarea", "autocomplete"].includes(controlType);

  const sanitizeValue = (value: any) => {
    if (typeof value !== "string" || !shouldSanitize) {
      return value;
    }

    return value
      .replace(/^\s+/, "")      // remove leading spaces
      .replace(/\s{2,}/g, " "); // only one consecutive space
  };

  const renderComponent = (field: any, error: any) => {
    const commonProps = {
      ...rest,
      field: {
        ...field,
        onChange: (value: any) => {
          const inputValue =
            value?.target?.value !== undefined
              ? value.target.value
              : value;

          field.onChange(
            typeof inputValue === "string"
              ? sanitizeValue(inputValue)
              : inputValue
          );
        },
        onBlur: (e?: any) => {
          if (shouldSanitize) {
            const value =
              e?.target?.value !== undefined
                ? sanitizeValue(e.target.value).trim()
                : typeof field.value === "string"
                  ? field.value.trim()
                  : field.value;

            field.onChange(value);
          }

          field.onBlur();
        },
        value: field.value,
        name: field.name,
        ref: field.ref,
      },
      error: !!error,
      helperText: error?.message,
      slotProps,
    };

    switch (controlType) {
      case "input":
        return <Input {...commonProps} />;

      case "password":
        return <Password {...commonProps} />;

      case "textarea":
        return <TextArea {...commonProps} />;

      case "autocomplete":
        return <AutoComplete {...commonProps} />;

      case "chip":
        return <ChipInput {...commonProps} />;

      case "select":
        return <Select {...commonProps} />;

      case "file":
        return <FileInput {...commonProps} />;

      case "multipleFile":
        return <MultipleFileInput {...commonProps} />;

      case "checkbox":
        return <Checkbox {...commonProps} />;

      case "range":
        return <Range {...commonProps} />;

      case "multiAssign":
        return (
          <MultiAssign
            {...commonProps}
            label={rest.label}
            list={rest.list || rest.options}
            idKey={rest.idKey}
            nameKey={rest.nameKey}
          />
        );

      case "date":
        return <DateInput {...commonProps} />;

      case "radio":
        return <RadioGroup {...commonProps} options={rest.options} />;

      case "toggle":
        return <Toggle {...commonProps} />;

      case "timePicker":
        return <TimePicker {...commonProps} />;

      case "radioAssign":
        return (
          <RadioAssign
            {...commonProps}
            heading={rest.heading}
            label={rest.label}
            list={rest.list}
            idKey={rest.idKey}
            nameKey={rest.nameKey}
            displayKey={rest.displayKey}
            setOption={rest.setOption}
            setValueChanged={rest.setValueChanged}
            defaultValue={rest.defaultValue}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => renderComponent(field, fieldState.error)}
    />
  )
};

export default FormControl;
